// File: clientapp/src/components/FileUpload.js
import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import DocumentResultRow from './DocumentResultRow';
import styles from './FileUpload.module.css';

export default function FileUpload() {
    const [selectedFile, setSelectedFile] = useState(null);
    const [masterId, setMasterId] = useState(null);
    const [documentResults, setDocumentResults] = useState([]);
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [classificationOptions, setClassificationOptions] = useState([]);

    const onDrop = useCallback(acceptedFiles => {
        if (acceptedFiles.length > 0) {
            setSelectedFile(acceptedFiles[0]);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

    // Fetch classification options when the component mounts.
    useEffect(() => {
        async function fetchClassifications() {
            try {
                const res = await fetch('/api/documentclassifications');
                const data = await res.json();
                setClassificationOptions(data);
            } catch (error) {
                console.error("Error fetching classifications", error);
            }
        }
        fetchClassifications();
    }, []);

    const handleUpload = async () => {
        if (!selectedFile) {
            alert("Please select a file.");
            return;
        }

        setIsLoading(true);

        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await fetch('/api/classification/upload', {
                method: 'POST',
                credentials: 'include',
                body: formData,
            });

            const data = await response.json();
            setMasterId(data.id);
            setDocumentResults(data.documents || []);
            setMessage("File uploaded and classified successfully!");
        } catch (error) {
            console.error("Upload failed", error);
            setMessage("Upload failed!");
        } finally {
            setIsLoading(false);
        }
    };

    const submitFeedback = async (documentId, isCorrect, correctedClassificationId) => {
        const payload = {
            ClassificationDocumentResultId: documentId,
            IsCorrect: isCorrect,
            CorrectClassificationId: correctedClassificationId
        };

        try {
            const response = await fetch('/api/classification/feedback', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const errorText = await response.text();
                console.error("Feedback submission failed", errorText);
            }
        } catch (error) {
            console.error("Feedback submission error", error);
        }
    };

    return (
        <div className={styles.container}>
            <h2>File Upload & Classification</h2>
            <div
                {...getRootProps()}
                className={`${styles.dropzone} ${isDragActive ? styles.dropzoneActive : ''}`}
            >
                <input {...getInputProps()} />
                {isDragActive ? <p>Drop the file here...</p> : <p>Drag 'n' drop a file here, or click to select one</p>}
            </div>
            {selectedFile && <div className={styles.selectedFile}><strong>Selected File:</strong> {selectedFile.name}</div>}
            <button onClick={handleUpload} className={styles.submitButton} disabled={isLoading}>
                {isLoading ? "Processing..." : "Submit"}
            </button>
            {message && <p className={styles.message}>{message}</p>}

            {documentResults && documentResults.length > 0 && (
                <div className={styles.results}>
                    <h3>Classification Results</h3>
                    <table className={styles.resultsTable}>
                        <thead>
                            <tr>
                                <th>Document Type</th>
                                <th>Page Range</th>
                                <th>Confidence</th>
                                <th>Correct?</th>                                
                            </tr>
                        </thead>
                        <tbody>
                            {documentResults.map(doc => (
                                <DocumentResultRow
                                    key={doc.id}
                                    doc={doc}
                                    onFeedbackSubmit={submitFeedback}
                                    classificationOptions={classificationOptions}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
